import { profileIncompleteMessage } from "@/features/students/applicant-profile";
import { ApiError } from "@/lib/api/client";
import type { Locale } from "@/i18n/config";
import { localizedText } from "@/i18n/text";

/**
 * Turns a failed apply into copy the student can act on.
 *
 * The backend tags these failures with a code because the status alone is
 * ambiguous — "you already applied" and "you have no slots left at this
 * company" are both 409, and they call for different next steps. The generic
 * message on the error is a developer-facing log line, so anything we recognise
 * gets its wording from here instead.
 */
export function applyErrorMessage(error: unknown, locale: Locale): string {
  const fallback = localizedText(
    locale,
    "Kunde inte skicka ansökan.",
    "Could not send the application."
  );

  if (!(error instanceof ApiError)) {
    return error instanceof Error && error.message ? error.message : fallback;
  }

  switch (error.code) {
    case "APPLICATION_LIMIT_REACHED": {
      const details = error.limitDetails;
      const max = details?.max;
      const company = details?.companyName;

      if (max != null && company) {
        return localizedText(
          locale,
          `Du har redan ${max} aktiva ansökningar hos ${company}, vilket är så många de tillåter samtidigt. Dra tillbaka en av dina ansökningar för att kunna söka den här bostaden.`,
          `You already have ${max} active applications with ${company}, which is as many as they allow at once. Withdraw one of your applications to apply for this home.`
        );
      }

      return localizedText(
        locale,
        "Du har redan så många aktiva ansökningar som det här företaget tillåter samtidigt. Dra tillbaka en av dem för att kunna söka den här bostaden.",
        "You already have as many active applications as this company allows at once. Withdraw one of them to apply for this home."
      );
    }

    case "ALREADY_APPLIED":
      return localizedText(
        locale,
        "Du har redan sökt den här bostaden.",
        "You have already applied for this home."
      );

    case "PROFILE_INCOMPLETE":
      return profileIncompleteMessage(error.missingFields, locale);

    case "NOT_IN_QUEUE":
      return localizedText(
        locale,
        "Du måste stå i företagets bostadskö för att kunna söka den här bostaden.",
        "You need to be in the company's housing queue to apply for this home."
      );

    case "EXTERNAL_SYSTEM_UNAVAILABLE":
      // Deliberately not phrased as the student's fault: nothing they did
      // caused it, and the action will very likely work on a retry.
      return localizedText(
        locale,
        "Vi kan inte nå företagets system just nu, så vi kan inte kontrollera dina ansökningar hos dem. Försök igen om en liten stund.",
        "We can't reach the company's system right now, so we can't check your applications with them. Please try again shortly."
      );

    case "LANDLORD_DOCUMENTS_REQUIRED": {
      const details = error.landlordDocuments;
      const documents = details?.documents ?? [];

      // Naming the documents is the whole gain over the provider's own
      // sentence, which mentions one document and says nothing about where to
      // fix it. The link that goes with this lives in applyErrorAction.
      if (documents.length > 0) {
        const named = documents
          .map((document) =>
            document.state === "EXPIRED" && document.expiresAt
              ? localizedText(
                  locale,
                  `${document.caption} (gick ut ${document.expiresAt})`,
                  `${document.caption} (expired ${document.expiresAt})`
                )
              : document.caption
          )
          .join(", ");

        return localizedText(
          locale,
          `Bostadsföretaget kräver dokument som saknas eller har gått ut: ${named}. Ansökan har inte skickats.`,
          `The company requires documents that are missing or expired: ${named}. The application was not submitted.`
        );
      }

      return localizedText(
        locale,
        "Bostadsföretaget kräver dokument som saknas eller har gått ut. Ansökan har inte skickats.",
        "The company requires documents that are missing or expired. The application was not submitted."
      );
    }

    case "AGE_REQUIREMENT_NOT_MET": {
      const { minAge = null, maxAge = null } = error.ageRequirement ?? {};

      // No applyErrorAction for this one: unlike an expired document, there is
      // nowhere to send the student and nothing for them to do about it.
      if (minAge != null && maxAge != null) {
        return localizedText(
          locale,
          `Den här bostaden kan bara sökas av dig som är mellan ${minAge} och ${maxAge} år.`,
          `This home can only be applied for if you are between ${minAge} and ${maxAge} years old.`
        );
      }
      if (minAge != null) {
        return localizedText(
          locale,
          `Den här bostaden kan bara sökas av dig som är minst ${minAge} år.`,
          `This home can only be applied for if you are at least ${minAge} years old.`
        );
      }
      if (maxAge != null) {
        return localizedText(
          locale,
          `Den här bostaden kan bara sökas av dig som är högst ${maxAge} år.`,
          `This home can only be applied for if you are at most ${maxAge} years old.`
        );
      }

      return localizedText(
        locale,
        "Du uppfyller inte åldersgränsen för den här bostaden.",
        "You do not meet the age requirement for this home."
      );
    }

    case "APPLICATION_PERIOD_CLOSED": {
      const applyBy = error.applyBy;

      // Answered without ever asking the landlord: the deadline came with the
      // listing. Naming the date matters — the advert was visible a moment ago,
      // so "for sent" alone reads as a bug rather than an explanation.
      if (applyBy) {
        return localizedText(
          locale,
          `Ansökningstiden för den här bostaden gick ut ${applyBy}. Annonsen hinner ligga kvar en stund efter att den stängts hos företaget.`,
          `The application period for this home closed on ${applyBy}. Listings can remain visible for a while after the company closes them.`
        );
      }

      return localizedText(
        locale,
        "Ansökningstiden för den här bostaden har gått ut.",
        "The application period for this home has closed."
      );
    }

    case "APPLICATION_REFUSED_BY_PROVIDER": {
      // Not the student's fault either, but — unlike the case above — retrying
      // will not help, so the copy must not invite it.
      const providerMessage = error.providerMessage;

      // The landlord's system says things we cannot: which document expired,
      // which requirement was unmet. When it does, its sentence is the one worth
      // showing — ours only has the status code to go on. The trailing sentence
      // is added either way, because that is the part that used to be a lie.
      if (providerMessage) {
        return localizedText(
          locale,
          `${providerMessage} Ansökan har inte skickats.`,
          `${providerMessage} The application was not submitted.`
        );
      }

      return localizedText(
        locale,
        "Bostadsföretagets system tog inte emot din ansökan. Annonsen kan ha stängts, eller så saknar din ansökan något företaget kräver. Ansökan har inte skickats.",
        "The company's system did not accept your application. The listing may have closed, or your application may be missing something the company requires. The application was not submitted."
      );
    }

    default:
      return error.message || fallback;
  }
}

/** Somewhere the student can go to resolve the failure themselves. */
export type ApplyErrorAction = {
  href: string;
  label: string;
};

/**
 * The link that turns a failed application into something actionable.
 *
 * Only one failure has a destination today: an expired or missing landlord
 * document, which the student can now renew from their account page. That was
 * the point of building the renewal flow — before it, the error named a
 * document and left the student with nowhere to take it.
 *
 * A sibling of {@link applyErrorMessage} rather than a change to it, so that
 * stays a plain string function and every existing call site keeps working.
 */
export function applyErrorAction(
  error: unknown,
  locale: Locale
): ApplyErrorAction | null {
  if (!(error instanceof ApiError) || error.code !== "LANDLORD_DOCUMENTS_REQUIRED") {
    return null;
  }

  const details = error.landlordDocuments;
  if (details?.companyId == null) {
    return null;
  }

  return {
    href: `/account?landlord=${details.companyId}#landlord-documents`,
    label: details.companyName
      ? localizedText(
          locale,
          `Hantera dina dokument hos ${details.companyName}`,
          `Manage your documents with ${details.companyName}`
        )
      : localizedText(
          locale,
          "Hantera dina dokument hos bostadsföretaget",
          "Manage your documents with the company"
        ),
  };
}
